/**
 * Enterprise Authentication Interceptor
 * Handles authentication with request queuing, exponential backoff, and circuit breaker pattern
 */

import { EventEmitter } from './browser-event-emitter';

interface QueuedRequest {
  id: string;
  url: string;
  config: RequestInit;
  resolve: (response: Response) => void;
  reject: (error: Error) => void;
  retryCount: number;
  timestamp: number;
}

interface CircuitBreakerState {
  failures: number;
  lastFailureTime: number;
  state: 'CLOSED' | 'OPEN' | 'HALF_OPEN';
  nextRetryTime: number;
}

class AuthenticationInterceptor extends EventEmitter {
  private static instance: AuthenticationInterceptor;
  private requestQueue: Map<string, QueuedRequest> = new Map();
  private isRefreshing = false;
  private refreshPromise: Promise<boolean> | null = null;
  private circuitBreaker: CircuitBreakerState = {
    failures: 0,
    lastFailureTime: 0,
    state: 'CLOSED',
    nextRetryTime: 0
  };

  // Configuration
  private readonly MAX_RETRIES = 3;
  private readonly INITIAL_RETRY_DELAY = 1000; // 1 second
  private readonly MAX_RETRY_DELAY = 30000; // 30 seconds
  private readonly CIRCUIT_BREAKER_THRESHOLD = 5;
  private readonly CIRCUIT_BREAKER_RESET_TIME = 60000; // 1 minute
  private readonly REQUEST_TIMEOUT = 30000; // 30 seconds

  private constructor() {
    super();
    this.setupPeriodicCleanup();
  }

  static getInstance(): AuthenticationInterceptor {
    if (!AuthenticationInterceptor.instance) {
      AuthenticationInterceptor.instance = new AuthenticationInterceptor();
    }
    return AuthenticationInterceptor.instance;
  }

  /**
   * Intercept and handle fetch requests with enterprise-grade error handling
   */
  async fetch(url: string, config: RequestInit = {}): Promise<Response> {
    // Check circuit breaker
    if (this.isCircuitOpen()) {
      throw new Error('Authentication service temporarily unavailable. Please try again later.');
    }

    // Add timeout to all requests
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.REQUEST_TIMEOUT);
    
    try {
      const enhancedConfig = {
        ...config,
        // API bodies and CSRF tokens must never be replayed to a redirect target.
        redirect: 'error' as RequestRedirect,
        signal: controller.signal,
        credentials: 'include' as RequestCredentials
      };

      // Add CSRF token for mutations
      if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(config.method || 'GET')) {
        const csrfToken = this.getCsrfToken();
        if (csrfToken) {
          enhancedConfig.headers = {
            ...enhancedConfig.headers,
            'X-CSRF-Token': csrfToken
          };
        }
      }

      // Make the request
      let response = await fetch(url, enhancedConfig);

      // Handle 401 Unauthorized
      if (response.status === 401 && !this.isAuthEndpoint(url)) {
        response = await this.handle401(url, enhancedConfig);
      }

      // Handle 403 CSRF errors
      if (response.status === 403) {
        const data = await response.clone().json().catch(() => ({}));
        if (data.error?.includes('CSRF')) {
          response = await this.handleCsrfError(url, enhancedConfig);
        }
      }

      // Reset circuit breaker on success
      if (response.ok) {
        this.resetCircuitBreaker();
      }

      return response;
    } catch (error) {
      this.recordFailure();
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Handle 401 responses with token refresh and request queuing
   */
  private async handle401(url: string, config: RequestInit): Promise<Response> {
    // If already refreshing, queue this request
    if (this.isRefreshing) {
      return this.queueRequest(url, config);
    }

    // Start refresh process
    this.isRefreshing = true;
    this.refreshPromise = this.refreshAccessToken();

    try {
      const refreshSuccess = await this.refreshPromise;
      
      if (refreshSuccess) {
        // Retry original request
        const response = await fetch(url, config);
        
        // Process queued requests
        this.processQueuedRequests();
        
        return response;
      } else {
        // Refresh failed - emit logout event
        this.emit('logout-required');
        throw new Error('Authentication failed. Please login again.');
      }
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  /**
   * Queue request while token is being refreshed
   */
  private queueRequest(url: string, config: RequestInit): Promise<Response> {
    return new Promise((resolve, reject) => {
      const requestId = `${url}-${Date.now()}-${Math.random()}`;
      
      this.requestQueue.set(requestId, {
        id: requestId,
        url,
        config,
        resolve,
        reject,
        retryCount: 0,
        timestamp: Date.now()
      });

      // Set timeout for queued request
      setTimeout(() => {
        const request = this.requestQueue.get(requestId);
        if (request) {
          this.requestQueue.delete(requestId);
          request.reject(new Error('Request timeout while waiting for authentication'));
        }
      }, this.REQUEST_TIMEOUT);
    });
  }

  /**
   * Process all queued requests after token refresh
   */
  private async processQueuedRequests(): Promise<void> {
    const requests = Array.from(this.requestQueue.values());
    this.requestQueue.clear();

    // Process requests with exponential backoff
    for (const request of requests) {
      const delay = this.calculateBackoffDelay(request.retryCount);
      
      setTimeout(async () => {
        try {
          const response = await fetch(request.url, request.config);
          request.resolve(response);
        } catch (error) {
          if (request.retryCount < this.MAX_RETRIES) {
            // Re-queue with increased retry count
            request.retryCount++;
            this.requestQueue.set(request.id, request);
          } else {
            request.reject(error as Error);
          }
        }
      }, delay);
    }
  }

  /**
   * Refresh access token with proper error handling
   */
  private async refreshAccessToken(): Promise<boolean> {
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        redirect: 'error',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Extract new CSRF token if provided
        const data = await response.json();
        if (data.csrfToken) {
          window.__CSRF_TOKEN = data.csrfToken;
        }
        return true;
      }

      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  }

  /**
   * Handle CSRF token errors with proper retry logic
   */
  private async handleCsrfError(url: string, config: RequestInit): Promise<Response> {
    try {
      // Get new CSRF token
      const csrfResponse = await fetch('/api/auth/csrf', {
        method: 'GET',
        redirect: 'error',
        credentials: 'include'
      });

      if (csrfResponse.ok) {
        const data = await csrfResponse.json();
        window.__CSRF_TOKEN = data.csrfToken;

        // Retry original request with new token
        const enhancedConfig = {
          ...config,
          headers: {
            ...config.headers,
            'X-CSRF-Token': data.csrfToken
          }
        };

        return fetch(url, enhancedConfig);
      }

      throw new Error('Failed to obtain CSRF token');
    } catch (error) {
      console.error('CSRF token refresh failed:', error);
      throw error;
    }
  }

  /**
   * Calculate exponential backoff delay
   */
  private calculateBackoffDelay(retryCount: number): number {
    const delay = Math.min(
      this.INITIAL_RETRY_DELAY * Math.pow(2, retryCount),
      this.MAX_RETRY_DELAY
    );
    // Add jitter to prevent thundering herd
    return delay + Math.random() * 1000;
  }

  /**
   * Circuit breaker pattern implementation
   */
  private isCircuitOpen(): boolean {
    if (this.circuitBreaker.state === 'OPEN') {
      // Check if it's time to try again
      if (Date.now() >= this.circuitBreaker.nextRetryTime) {
        this.circuitBreaker.state = 'HALF_OPEN';
        return false;
      }
      return true;
    }
    return false;
  }

  private recordFailure(): void {
    this.circuitBreaker.failures++;
    this.circuitBreaker.lastFailureTime = Date.now();

    if (this.circuitBreaker.failures >= this.CIRCUIT_BREAKER_THRESHOLD) {
      this.circuitBreaker.state = 'OPEN';
      this.circuitBreaker.nextRetryTime = Date.now() + this.CIRCUIT_BREAKER_RESET_TIME;
      this.emit('circuit-open', {
        failures: this.circuitBreaker.failures,
        nextRetryTime: new Date(this.circuitBreaker.nextRetryTime)
      });
    }
  }

  private resetCircuitBreaker(): void {
    if (this.circuitBreaker.state !== 'CLOSED') {
      this.emit('circuit-closed');
    }
    this.circuitBreaker = {
      failures: 0,
      lastFailureTime: 0,
      state: 'CLOSED',
      nextRetryTime: 0
    };
  }

  /**
   * Check if URL is an authentication endpoint
   */
  private isAuthEndpoint(url: string): boolean {
    const authPaths = ['/api/auth/', '/api/login', '/api/register', '/api/refresh'];
    return authPaths.some(path => url.includes(path));
  }

  /**
   * Get CSRF token from window
   */
  private getCsrfToken(): string | null {
    // Prefer in-memory token
    const fromWindow = (window as any).__CSRF_TOKEN || null;
    if (fromWindow) return fromWindow;
    // Fallback to cookie (boltos_csrf_token)
    try {
      const match = document.cookie.match(/(?:^|; )boltos_csrf_token=([^;]+)/);
      return match ? decodeURIComponent(match[1]) : null;
    } catch {
      return null;
    }
  }

  /**
   * Periodic cleanup of stale queued requests
   */
  private setupPeriodicCleanup(): void {
    setInterval(() => {
      const now = Date.now();
      const staleRequests: string[] = [];

      this.requestQueue.forEach((request, id) => {
        if (now - request.timestamp > this.REQUEST_TIMEOUT) {
          staleRequests.push(id);
          request.reject(new Error('Request expired in queue'));
        }
      });

      staleRequests.forEach(id => this.requestQueue.delete(id));
    }, 60000); // Run every minute
  }

  /**
   * Get interceptor statistics for monitoring
   */
  getStatistics() {
    return {
      queuedRequests: this.requestQueue.size,
      circuitBreakerState: this.circuitBreaker.state,
      circuitBreakerFailures: this.circuitBreaker.failures,
      isRefreshing: this.isRefreshing
    };
  }
}

// Export singleton instance
export const authInterceptor = AuthenticationInterceptor.getInstance();

// Extend Window interface
declare global {
  interface Window {
    __CSRF_TOKEN?: string;
  }
}
